import React, { useEffect, useState, useCallback, useMemo, useRef, useContext } from 'react';
import { IPropertyChild, getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { isArrayFloatEditor, isNullableType } from './utils/InputTypeUtils';
import { parseInputToNumber, cleanTrailingDecimals, hasTrailingDecimal } from '../../utils/NumericFormattingUtils';
import { EditorLabel } from './EditorLabel';
import classNames from 'classnames';
import { useTrackFocus } from 'components/inputs/hooks/useTrackFocus';
import { EditorValidationWrapper } from './EditorValidationWrapper';
import { debounce } from 'debounce';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { useTabbingContext } from 'contexts/TabbingContext';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { setNumericArrayValue } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { AwaitableContext } from 'contexts/AwaitableContext';

interface INumericArrayEditorProps extends IEditorProps {}

const splitValueToLines = (child: IPropertyChild) => {
  return getChildValueAsArray(child).join('\n');
};

const parseValue = (value: string, editor: IPropertyChild) =>
  parseInputToNumber(value, isArrayFloatEditor(editor), isNullableType(editor));

const ensureNumericValues = (value: string, editor: IPropertyChild) => {
  const split = value.split('\n');
  const cleaned = split.map(val => {
    return val ? parseValue(val, editor) : val;
  });
  return cleaned.join('\n');
};

const getFieldClass = (isFocused: boolean) =>
  classNames('dpc-text-area', 'dpc-text-input', 'dpc-text-input-wrapper', 'dpc-editor-text-area__field', {
    'dpc-text-input-wrapper--focused': isFocused,
  });

/**
 * Similar to a String Array Editor, but because of the numeric value handling, it needs to be
 * have handlers chained together differently, and thus needs to be a separate component.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function NumericArrayEditor({ autoFocus, child, id, hideInputLabel, path }: INumericArrayEditorProps) {
  const tabIndex = useTabbingContext();
  const { editingContextId } = useContext(PropertyEditorContext);
  const numericType = getEditorConfigValue(child, 'NumericType');
  const [fieldValue, setFieldValue] = useState('');
  const { addPromise, removePromise } = useContext(AwaitableContext);

  const emitValue = useCallback(
    async (value: string, path: string[]) => {
      const splitValue = value
        .split('\n')
        .filter(val => val)
        .map(val => cleanTrailingDecimals(val));

      const id = path.join(',');
      addPromise(
        id,
        (async () => {
          const issues = await setNumericArrayValue({
            editingContextId,
            path,
            value: splitValue,
            NumericArrayType: numericType,
          });
          // because we get these back so fast, we need to re-render with them?
          child.ValidationIssues = issues || [];
          removePromise(id);
        })()
      );
    },
    [addPromise, editingContextId, numericType, removePromise, child]
  );
  const debouncedEmit = useMemo(() => debounce(emitValue, 500), [emitValue]);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // they've moved on, so clean it up and send it out
      debouncedEmit.clear();
      emitValue(e.target.value || '', path);
    },
    [debouncedEmit, emitValue, path]
  );
  const { isFocused, focusTrackingProps } = useTrackFocus({ onBlur });
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value;
      if (hasTrailingDecimal(value)) {
        // they are almost certainly still entering data
        setFieldValue(value);
        return debouncedEmit.clear();
      }
      value = ensureNumericValues(value, child);
      setSelectionEnd(e.target.selectionEnd);
      setFieldValue(value);
      debouncedEmit.clear();
      debouncedEmit(value, path);
    },
    [child, debouncedEmit, path]
  );

  useEffect(() => {
    // if we have replaced the field value, the cursor back where it was:
    if (inputRef.current && selectionEnd !== null) {
      inputRef.current.setSelectionRange(selectionEnd, selectionEnd);
    }
  }, [selectionEnd, setFieldValue]);

  useEffect(() => {
    if (!isFocused) {
      setFieldValue(splitValueToLines(child));
    }
    //skip updating the field from server while it has focus...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child]);

  return (
    <EditorLabel editor={child} path={path} htmlFor={id} hideInputLabel={hideInputLabel}>
      <EditorValidationWrapper editor={child}>
        <textarea
          autoFocus={autoFocus}
          ref={inputRef}
          className={getFieldClass(isFocused)}
          value={fieldValue}
          onChange={handleChange}
          tabIndex={tabIndex}
          {...focusTrackingProps}
        />
      </EditorValidationWrapper>
    </EditorLabel>
  );
}
